import { useState } from "react";
import { api, API } from "../lib/api";
import { notifyQuotaExhausted } from "../lib/quota";
import { Loader2 } from "lucide-react";
import { CopyReadingButton } from "../components/CopyReadingButton";
import { DeleteReadingButton } from "../components/DeleteReadingButton";
import { toast } from "sonner";

const CATEGORIES = [
  { id: "amore", label: "❤️ Amore" },
  { id: "lavoro", label: "💼 Lavoro e finanze" },
  { id: "generale", label: "🔮 Consigli generali" },
];
const CAT_LABELS = Object.fromEntries(CATEGORIES.map((c) => [c.id, c.label]));
const ORDINALS = ["primo", "secondo", "terzo", "quarto", "quinto", "sesto"];

const HexLines = ({ pattern, moving = [], testid }) => (
  <div className="flex flex-col-reverse items-center gap-1.5" data-testid={testid}>
    {pattern.split("").map((bit, i) => {
      const pos = i + 1;
      const isMobile = moving.includes(pos);
      return (
        <div key={pos} className="flex items-center gap-2">
          {bit === "1" ? (
            <div className="w-24 h-2.5 rounded-full bg-amber-300" />
          ) : (
            <div className="flex gap-3">
              <div className="w-[42px] h-2.5 rounded-full bg-amber-300" />
              <div className="w-[42px] h-2.5 rounded-full bg-amber-300" />
            </div>
          )}
          {isMobile && <span className="text-amber-400 text-xs" title="linea mobile">✦</span>}
        </div>
      );
    })}
  </div>
);

export default function IChing() {
  const [phase, setPhase] = useState("coins"); // coins → assign → topic → throws → result
  const [faceTwo, setFaceTwo] = useState(null); // "Testa" | "Croce"
  const [category, setCategory] = useState("");
  const [question, setQuestion] = useState("");
  const [session, setSession] = useState(null);
  const [throwsList, setThrowsList] = useState([]); // somme confermate
  const [coinVals, setCoinVals] = useState([null, null, null]);
  const [result, setResult] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const faceThree = faceTwo === "Testa" ? "Croce" : "Testa";

  const startConsultation = async () => {
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/iching/sessions", {
        category, question: question.trim(), face_two: faceTwo,
      });
      setSession(data);
      setPhase("throws");
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 401) { window.location.href = "/accedi"; return; }
      if (d?.code === "quota_esaurita") { notifyQuotaExhausted(); return; }
      setError(typeof d === "string" ? d : "Errore di connessione. Riprova.");
    } finally {
      setBusy(false);
    }
  };

  const confirmThrow = async () => {
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post(`/iching/sessions/${session.id}/throw`, { values: coinVals });
      const next = [...throwsList, data.total];
      setThrowsList(next);
      setCoinVals([null, null, null]);
      if (data.done) {
        setResult(data.result);
        setPhase("result");
        interpret();
      }
    } catch (e) {
      setError(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Errore di connessione. Riprova.");
    } finally {
      setBusy(false);
    }
  };

  const interpret = async () => {
    try {
      const res = await fetch(`${API}/iching/sessions/${session.id}/interpret`, {
        method: "POST", credentials: "include",
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          if (!p.startsWith("data: ")) continue;
          const ev = JSON.parse(p.slice(6));
          if (ev.type === "meta") setResult(ev.result);
          else if (ev.type === "delta") setText((t) => t + ev.content);
          else if (ev.type === "error") setError(ev.message);
        }
      }
    } catch {
      setError("Errore durante l'interpretazione. Riprova.");
    }
  };

  const reset = () => {
    setPhase("coins"); setFaceTwo(null); setCategory(""); setQuestion("");
    setSession(null); setThrowsList([]); setCoinVals([null, null, null]);
    setResult(null); setText(""); setError("");
  };

  return (
    <div className="min-h-screen px-4 py-10 sm:py-14">
      <div className="max-w-2xl mx-auto">
        <p className="text-center text-amber-300/70 text-[11px] tracking-[0.3em] uppercase" data-testid="iching-eyebrow">Metodo tradizionale delle tre monete</p>
        <h1 className="font-display text-4xl sm:text-5xl text-amber-100 text-center mt-3" data-testid="iching-title">I Ching</h1>

        {error && <p className="text-red-300 text-sm text-center mt-6" data-testid="iching-error">{error}</p>}

        {phase === "coins" && (
          <div className="mystic-card p-7 sm:p-9 mt-8 text-center" data-testid="iching-coins-step">
            <p className="text-amber-100 text-lg leading-relaxed">Per effettuare questa consultazione ti servono <strong>3 monete</strong>.</p>
            <p className="text-purple-200/80 text-sm leading-relaxed mt-4">
              Se possiedi le monete specifiche per l'I Ching, puoi utilizzarle. Altrimenti vanno benissimo
              3 monete normali uguali tra loro, anche tre monete da 1 centesimo. L'importante è che siano
              dello stesso tipo e della stessa misura, con due facce ben distinguibili.
            </p>
            <p className="text-purple-200/80 text-sm leading-relaxed mt-3">
              I lanci delle monete li farai tu, fisicamente: Arcana registra i tuoi risultati e costruisce
              l'esagramma senza mai inventarli né modificarli.
            </p>
            <button data-testid="iching-have-coins-btn" onClick={() => setPhase("assign")}
              className="gold-btn px-8 py-3.5 text-sm mt-8 tracking-widest uppercase">
              Sì, ho 3 monete
            </button>
          </div>
        )}

        {phase === "assign" && (
          <div className="mystic-card p-7 sm:p-9 mt-8" data-testid="iching-assign-step">
            <p className="text-purple-200/80 text-sm leading-relaxed text-center">
              Ogni moneta ha due facce. Stabilisci ora quale faccia vale <strong className="text-amber-200">2</strong> e quale vale <strong className="text-amber-200">3</strong>:
              la scelta è libera, ma una volta fatta dovrai usarla per tutti e sei i lanci della consultazione.
            </p>
            <p className="font-display text-amber-100 text-xl text-center mt-8">Quale faccia vale 2?</p>
            <div className="grid grid-cols-2 gap-3 mt-4">
              {["Testa", "Croce"].map((f) => (
                <button key={f} data-testid={`iching-face2-${f.toLowerCase()}`} onClick={() => setFaceTwo(f)}
                  className={`py-3.5 rounded-full border text-sm tracking-wide transition-colors ${faceTwo === f ? "bg-[#d4a853] text-[#1a0b2e] font-bold border-[#d4a853]" : "border-amber-500/40 text-amber-200 hover:bg-amber-500/10"}`}>
                  {f}
                </button>
              ))}
            </div>
            {faceTwo && (
              <>
                <p className="font-display text-amber-100 text-xl text-center mt-7">Quale faccia vale 3?</p>
                <div className="mt-4 flex justify-center">
                  <span data-testid="iching-face3-auto" className="py-3.5 px-10 rounded-full border border-amber-500/40 text-amber-200 text-sm tracking-wide bg-amber-500/5">
                    {faceThree}
                  </span>
                </div>
                <div className="mt-7 rounded-xl border border-amber-500/25 bg-black/20 p-5 text-center">
                  <p className="text-purple-300/70 text-xs tracking-[0.25em] uppercase">La tua assegnazione</p>
                  <p className="text-amber-100 text-lg mt-2" data-testid="iching-assignment-summary">{faceTwo} = 2 &nbsp;·&nbsp; {faceThree} = 3</p>
                </div>
                <button data-testid="iching-confirm-assignment-btn" onClick={() => setPhase("topic")}
                  className="gold-btn w-full py-3.5 text-sm mt-6 tracking-widest uppercase">
                  Conferma e continua
                </button>
              </>
            )}
          </div>
        )}

        {phase === "topic" && (
          <div className="mystic-card p-7 sm:p-9 mt-8" data-testid="iching-topic-step">
            <p className="font-display text-amber-100 text-xl text-center">Di cosa vuoi sapere?</p>
            <div className="flex flex-col gap-3 mt-5">
              {CATEGORIES.map((c) => (
                <button key={c.id} data-testid={`iching-cat-${c.id}`} onClick={() => setCategory(c.id)}
                  className={`py-3.5 px-5 rounded-full border text-sm tracking-wide transition-colors text-left ${category === c.id ? "bg-[#d4a853] text-[#1a0b2e] font-bold border-[#d4a853]" : "border-amber-500/40 text-amber-200 hover:bg-amber-500/10"}`}>
                  {c.label}
                </button>
              ))}
            </div>
            <p className="font-display text-amber-100 text-xl text-center mt-8">Scrivi la tua domanda</p>
            <textarea data-testid="iching-question-input" value={question} onChange={(e) => setQuestion(e.target.value)}
              rows={4} maxLength={600} placeholder="Scrivi qui la tua domanda…"
              className="w-full mt-4 rounded-xl bg-black/25 border border-amber-500/25 text-amber-50 placeholder-purple-300/40 p-4 text-sm leading-relaxed focus:outline-none focus:border-amber-400/60" />
            <button data-testid="iching-start-btn" onClick={startConsultation}
              disabled={busy || !category || question.trim().length < 2}
              className="gold-btn w-full py-3.5 text-sm mt-6 tracking-widest uppercase disabled:opacity-50">
              {busy ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "OK — Inizia la consultazione"}
            </button>
          </div>
        )}

        {phase === "throws" && session && (
          <div className="mt-8 space-y-6" data-testid="iching-throws-step">
            <div className="mystic-card p-7">
              <p className="text-center text-amber-300/70 text-[11px] tracking-[0.3em] uppercase">Lancio {throwsList.length + 1} di 6</p>
              <p className="text-purple-200/80 text-sm leading-relaxed text-center mt-3">
                Lancia fisicamente le tue tre monete, contemporaneamente. Poi inserisci qui sotto il valore uscito
                per ciascuna moneta. Ricorda la tua assegnazione: <strong className="text-amber-200">{session.faces["2"]} = 2 · {session.faces["3"]} = 3</strong>
              </p>
              <div className="mt-6 space-y-4">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="flex items-center justify-between gap-3" data-testid={`iching-coin-${i + 1}`}>
                    <span className="text-amber-100 text-sm w-20">Moneta {i + 1}</span>
                    <div className="flex gap-2">
                      {[2, 3].map((v) => (
                        <button key={v} data-testid={`iching-coin-${i + 1}-val-${v}`}
                          onClick={() => setCoinVals((cv) => cv.map((x, j) => (j === i ? v : x)))}
                          className={`w-14 py-2.5 rounded-full border text-sm font-bold transition-colors ${coinVals[i] === v ? "bg-[#d4a853] text-[#1a0b2e] border-[#d4a853]" : "border-amber-500/40 text-amber-200 hover:bg-amber-500/10"}`}>
                          {v}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <button data-testid="iching-confirm-throw-btn" onClick={confirmThrow}
                disabled={busy || coinVals.some((v) => v === null)}
                className="gold-btn w-full py-3.5 text-sm mt-7 tracking-widest uppercase disabled:opacity-50">
                {busy ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "Conferma lancio"}
              </button>
            </div>

            <div className="mystic-card p-7" data-testid="iching-progress">
              <p className="text-center text-amber-300/70 text-[11px] tracking-[0.3em] uppercase">Esagramma in costruzione</p>
              <div className="mt-4 space-y-2">
                {[0, 1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="flex items-center justify-between text-sm border-b border-purple-500/10 pb-2" data-testid={`iching-progress-row-${i + 1}`}>
                    <span className="text-purple-200/70">Lancio {i + 1}</span>
                    <span className={throwsList[i] !== undefined ? "text-amber-200 font-bold" : "text-purple-300/40 italic"}>
                      {throwsList[i] !== undefined ? throwsList[i] : "ATTENDO"}
                    </span>
                  </div>
                ))}
              </div>
              {throwsList.length > 0 && (
                <div className="mt-5 flex justify-center">
                  <HexLines testid="iching-progress-lines"
                    pattern={(() => { const p = throwsList.map((t) => ((t === 7 || t === 9) ? "1" : "0")).join(""); return p.padEnd(6, "").split("").map((ch) => ch || null); })() && throwsList.map((t) => ((t === 7 || t === 9) ? "1" : "0")).join("") + "0".repeat(6 - throwsList.length)}
                    moving={throwsList.map((t, i) => ((t === 6 || t === 9) ? i + 1 : 0)).filter(Boolean)} />
                </div>
              )}
            </div>
          </div>
        )}

        {phase === "result" && result && (
          <div className="mt-8 space-y-6" data-testid="iching-result-step">
            <div className="mystic-card p-7">
              <p className="text-purple-300/70 text-xs tracking-[0.25em] uppercase">La tua domanda</p>
              <p className="text-amber-50 text-base mt-2 leading-relaxed" data-testid="iching-result-question">{question}</p>
              <p className="text-purple-300/70 text-xs tracking-[0.25em] uppercase mt-5">Ambito scelto</p>
              <p className="text-amber-100 mt-1" data-testid="iching-result-category">{CAT_LABELS[session.category]}</p>
            </div>

            <div className="mystic-card p-7 text-center" data-testid="iching-result-main">
              <p className="text-purple-300/70 text-xs tracking-[0.25em] uppercase">Esagramma principale</p>
              <p className="font-display text-3xl text-amber-100 mt-3">n. {result.main.num} — {result.main.name}</p>
              <div className="mt-5 flex justify-center">
                <HexLines testid="iching-main-lines" pattern={result.main.pattern}
                  moving={result.moving_lines.map((m) => m.position)} />
              </div>
              <p className="text-purple-200/70 text-sm mt-5">Trigramma inferiore: {result.main.lower_trigram[1]} · Trigramma superiore: {result.main.upper_trigram[1]}</p>
            </div>

            <div className="mystic-card p-7" data-testid="iching-result-moving">
              <p className="text-purple-300/70 text-xs tracking-[0.25em] uppercase text-center">Linee mobili</p>
              {result.moving_lines.length === 0 ? (
                <p className="text-amber-100 text-sm text-center mt-4">Nessuna linea mobile: l'esagramma <strong>non cambia</strong>.</p>
              ) : (
                <div className="mt-4 space-y-4">
                  {result.moving_lines.map((m) => (
                    <div key={m.position} className="rounded-xl border border-amber-500/20 bg-black/20 p-4" data-testid={`iching-moving-${m.position}`}>
                      <p className="text-amber-200 text-sm font-bold">Linea mobile al {ORDINALS[m.position - 1]} posto — {m.value} <span className="font-normal text-purple-200/70">({m.label})</span></p>
                      <p className="text-purple-100/85 text-sm mt-2 leading-relaxed">{m.text}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="mystic-card p-7 text-center" data-testid="iching-result-transformed">
              <p className="text-purple-300/70 text-xs tracking-[0.25em] uppercase">Esagramma trasformato</p>
              {result.transformed ? (
                <>
                  <p className="text-amber-300 text-sm mt-3 tracking-widest uppercase">L'esagramma cambia</p>
                  <p className="text-purple-200/80 text-sm mt-2">n. {result.main.num} {result.main.name} → n. {result.transformed.num} {result.transformed.name}</p>
                  <p className="font-display text-2xl text-amber-100 mt-3">n. {result.transformed.num} — {result.transformed.name}</p>
                  <div className="mt-5 flex justify-center">
                    <HexLines testid="iching-transformed-lines" pattern={result.transformed.pattern} />
                  </div>
                </>
              ) : (
                <p className="text-amber-100 text-sm mt-4" data-testid="iching-no-transform">La situazione resta rappresentata dall'esagramma principale: non è presente un esagramma trasformato.</p>
              )}
            </div>

            <div className="mystic-card p-7" data-testid="iching-interpretation">
              <p className="text-purple-300/70 text-xs tracking-[0.25em] uppercase text-center">Interpretazione</p>
              {text ? (
                <p className="text-amber-50/90 text-sm sm:text-base leading-relaxed mt-4 whitespace-pre-wrap">{text}</p>
              ) : (
                <p className="text-purple-200/60 text-sm text-center mt-4 flex items-center justify-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Arcana sta interpretando il tuo esagramma…
                </p>
              )}
              {text && (
                <div className="mt-5 flex justify-center gap-2 flex-wrap">
                  <CopyReadingButton testid="copy-reading-iching" text={[
                    `La mia domanda: ${question}`,
                    `Ambito: ${CAT_LABELS[session?.category] || ""}`,
                    `Esagramma principale: n. ${result.main.num} — ${result.main.name}`,
                    ...result.moving_lines.map((m) => `Linea mobile al ${ORDINALS[m.position - 1]} posto — ${m.value} (${m.label}): ${m.text}`),
                    result.transformed
                      ? `Esagramma trasformato: n. ${result.transformed.num} — ${result.transformed.name}`
                      : "L'esagramma non cambia: nessun esagramma trasformato.",
                    "", "Interpretazione:", text,
                  ].join("\n")} />
                  {session?.id && (
                    <DeleteReadingButton testid="delete-reading-iching" onDelete={async () => {
                      await api.delete(`/iching/sessions/${session.id}`);
                      toast.success("Testo eliminato.");
                      reset();
                    }} />
                  )}
                </div>
              )}
            </div>

            <button data-testid="iching-new-consult-btn" onClick={reset}
              className="w-full py-3.5 rounded-full border border-amber-500/40 text-amber-200 hover:bg-amber-500/10 transition-colors text-sm tracking-widest uppercase">
              Nuova consultazione
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
